import { useCallback } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { addProductToShoppingCart } from "../services/shoppingCartService";
import { removeShoppingCartItem } from "../services/signalRService";
import {
  getCurrentUserFromLocalStorage,
  getI18nLanguageFromLocalStorage,
} from "../helpers/localStorageHelper";
import { ErrorTemplate, SuccessTemplate } from "../components/common/ToastMessage/Template";

export function useShoppingCartActions({ toast, translations }) {
  const lang = getI18nLanguageFromLocalStorage();

  const addToCart = useCallback(
    async (productId, quantity = 1, { courseTitle } = {}) => {
      const currentUser = getCurrentUserFromLocalStorage();
      if (!currentUser) {
        window.location.href = `/auth/login?lang=${lang}&returnUrl=${window.location.pathname}`;
        return false;
      }
      try {
        await addProductToShoppingCart(productId, quantity);
        if (toast?.current && translations?.ADD_SUCCESS) {
          toast.current.show({
            severity: "success",
            detail: translations.ADD_SUCCESS.replace(
              "{{name}}",
              courseTitle ?? ""
            ),
            life: 3000,
            className: "keduall-toast",
            content: (props) => SuccessTemplate(props),
          });
        }
        return true;
      } catch (error) {
        if (toast?.current && translations?.ADD_FAIL) {
          toast.current.show({
            severity: "error",
            detail: translations.ADD_FAIL,
            life: 3000,
            className: "keduall-toast",
            content: (props) => ErrorTemplate(props),
          });
        }
        return false;
      }
    },
    [lang, toast, translations]
  );

  const removeFromCart = useCallback(
    async (item, { setLoading, closeDialog } = {}) => {
      if (setLoading) setLoading(true);
      try {
        await removeShoppingCartItem(item?.productId);
        if (toast?.current && translations?.removeItemSuccessLabel) {
          toast.current.show({
            severity: "success",
            detail: translations.removeItemSuccessLabel.replace(
              "{{title}}",
              item?.name ?? ""
            ),
            life: 3000,
            className: "keduall-toast",
            content: (props) => SuccessTemplate(props),
          });
        }
        return true;
      } catch (error) {
        return false;
      } finally {
        if (setLoading) setLoading(false);
        if (closeDialog) closeDialog();
      }
    },
    [toast, translations]
  );

  const confirmRemoveFromCart = useCallback(
    (item, { onConfirm, deleteLoadingRef, isRealTimeLecture } = {}) => {
      const itemName = item?.name;
      // Use removeTicketLabel for real-time lectures, removeItemLabel for others
      const messageTemplate = isRealTimeLecture 
        ? (translations.removeTicketLabel || translations.removeItemLabel)
        : translations.removeItemLabel;
      const message = messageTemplate;
      
      const [before, after] = message.includes("{{title}}") 
        ? message.split("{{title}}")
        : message.includes("{{name}}")
        ? message.split("{{name}}")
        : [message, ""];
      
      confirmDialog({
        header: translations.removeCartItemHeader,
        message: (
          <span>
            {before}
            <strong>{itemName}</strong>
            {after}
          </span>
        ),
        accept: async () => {
          if (onConfirm) {
            await onConfirm();
          }
        },
        reject: () => {
          // Dialog will auto close
        },
        acceptLabel: translations.confirmButtonLabel,
        rejectLabel: translations.cancelButtonLabel,
        acceptClassName: "keduall-buttons button-filled-default-normal-primary",
        rejectClassName: "keduall-buttons button-text-normal-secondary",
        draggable: false,
      });
    },
    [translations]
  );

  return { addToCart, removeFromCart, confirmRemoveFromCart };
}

export async function performWithLoadingAndDialog(action, { setLoading, closeDialog } = {}) {
  if (setLoading) setLoading(true);
  try {
    await action();
  } finally {
    if (setLoading) setLoading(false);
    if (closeDialog) closeDialog();
  }
}


